import { memo, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Banknote, ReceiptText, CalendarMinus, FileWarning, LogOut, Receipt, TrendingUp, UserPlus, Users } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { reportAnchor, reportRange, shiftReportAnchor } from "../../lib/reportRange.js";
import { SummaryStatSuffix } from "../../components/SummaryStatCard.js";
import { DropdownSelect } from "../../components/DropdownButton.js";
import { Badge } from "../../components/ui/badge.js";
import { Button } from "../../components/ui/button.js";
import { Card } from "../../components/ui/card.js";
import { SearchInput } from "../../components/ui/search-input.js";
import { SpecularButton } from "../../components/ui/specular-button.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table.js";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs.js";
import type { PaginationMeta } from "../../components/ui/pagination.js";
import {
  FilterBar, ErrorBanner, OrgFilters, ExportButton, CardGrid, ReportStatCard, ReportTableFrame, ReportFooter, PageSizeSelect,
  TableScrollStart, TableToolbar, dropdownProps, stickyName, stickyCode, NAME_WIDTH, CODE_WIDTH, type OpenState,
} from "../../components/reports/ReportKit.js";
import {
  useUrlFilters, useDebouncedSearch, useReport, useOrgMeta, toParams, downloadReport,
} from "../../components/reports/useReport.js";
import { SalaryBreakupDialog } from "./SalaryBreakupDialog.js";
import { BASIS_OPTIONS, fmtAsOf, type PayBasis, type PayToDate } from "./payBasis.js";

type ReconStatus = "Matched" | "Mismatch" | "Missing in payroll" | "Not in HRMS";

interface InputRow {
  id: string; code: string; name: string; department: string | null; office: string | null;
  joinDate: string | null; exitDate: string | null; workingDays: number; payableDays: number; lopDays: number;
  monthlyGross: number | null; perDay: number | null; inHandMonthly: number | null; inHandPayable: number | null; toDate: PayToDate;
  ctcChange: { from: number; to: number; effective: string; reason: string } | null;
  reimbursement: number; settlement: "pending" | "done" | null; changed: boolean;
}
interface ReconRow {
  id: string | null; code: string; name: string; department: string | null; office: string | null;
  payableDays: number | null; workingDays: number | null; monthlyGross: number | null;
  expectedGross: number | null; uploadedGross: number | null; difference: number | null;
  status: ReconStatus; note: string | null;
}
interface PayrollReport {
  month: string; daysInMonth: number; asOf: string | null; view: "inputs" | "reconciliation";
  run: { uploaded: boolean; sources: string[]; lineCount: number };
  rows: (InputRow | ReconRow)[];
  /** Loaded by a separate request (`part=cards`), so a page turn never refetches it. */
  cards?: {
    employees: number; joiners: number; exits: number; ctcChanges: number; totalLop: number; reimbursements: number;
    pendingSettlements: number; mismatches: number | null; missingInPayroll: number | null; notInHrms: number | null;
  };
  meta: PaginationMeta;
}

const DEFAULTS = {
  search: "", officeIds: "", departmentIds: "", anchor: "", view: "inputs", changes: "all", basis: "toDate", page: "1", limit: "25",
};
// The cards cover the whole month; the tab and "changes only" narrow only the table.
const ROW_ONLY = ["view", "changesOnly"];

const API = "/reports/hr/payroll";

const CHANGE_OPTIONS = [
  { value: "all", label: "All employees" },
  { value: "changes", label: "Changes only" },
];

const MONEY = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });
const money = (n: number | null | undefined) => (n == null ? "—" : MONEY.format(n));

const STATUS_TONE: Record<ReconStatus, "success" | "error" | "warning" | "accent"> = {
  Matched: "success", Mismatch: "error", "Missing in payroll": "warning", "Not in HRMS": "accent",
};

const NUM = "px-2 text-center tabular-nums";

/** The last, sticky column: an icon that opens the employee’s salary breakup for the month. */
function BreakupCell({ id, name, onBreakup }: { id: string | null; name: string; onBreakup: (id: string) => void }) {
  return (
    <TableCell className="sticky right-0 z-10 bg-surface-0 px-2 text-center group-hover:bg-surface-50">
      {id && (
        <Button variant="ghost" size="icon" className="h-7 w-7 text-brand-600" onClick={() => onBreakup(id)} aria-label={`Salary breakup for ${name}`} title="Salary breakup">
          <ReceiptText size={15} />
        </Button>
      )}
    </TableCell>
  );
}

const BREAKUP_HEAD = "sticky right-0 z-10 bg-surface-50 px-2 text-center";

const InputTableRow = memo(function InputTableRow({ row, basis, onBreakup }: { row: InputRow; basis: PayBasis; onBreakup: (id: string) => void }) {
  const t = row.toDate;
  return (
    <TableRow className="group hover:bg-surface-50">
      <TableCell className={cn(stickyName, "bg-surface-0 group-hover:bg-surface-50")}>
        <div className={cn(NAME_WIDTH, "truncate font-semibold text-surface-900")} title={[row.name, row.department, row.office].filter(Boolean).join(" · ")}>{row.name}</div>
      </TableCell>
      <TableCell className={cn(stickyCode, "bg-surface-0 text-surface-600 group-hover:bg-surface-50")}>
        <div className={cn(CODE_WIDTH, "truncate")}>{row.code}</div>
      </TableCell>
      <TableCell className="px-3 text-surface-700"><div className="max-w-[160px] truncate">{row.department ?? "—"}</div></TableCell>
      <TableCell className="px-3 text-surface-700">{row.office ?? "—"}</TableCell>
      <TableCell className={cn(NUM, "border-l border-surface-200 whitespace-nowrap")}>
        {row.joinDate ? <Badge tone="success">{row.joinDate}</Badge> : <span className="text-surface-300">—</span>}
      </TableCell>
      <TableCell className={cn(NUM, "whitespace-nowrap")}>
        {row.exitDate ? <Badge tone="warning">{row.exitDate}</Badge> : <span className="text-surface-300">—</span>}
      </TableCell>
      {basis === "month" ? (
        <>
          <TableCell className={cn(NUM, "border-l border-surface-200 font-semibold", row.payableDays < row.workingDays && "text-[var(--color-warning-fg)]")}>
            {row.payableDays}<span className="font-normal text-surface-500"> / {row.workingDays}</span>
          </TableCell>
          <TableCell className={cn(NUM, row.lopDays > 0 && "font-semibold text-[var(--color-warning-fg)]")}>{row.lopDays}</TableCell>
        </>
      ) : (
        <>
          <TableCell
            className={cn(NUM, "border-l border-surface-200 font-semibold", t.payable < t.workingDays && "text-[var(--color-warning-fg)]")}
            title={`${t.payable} earned of ${t.workingDays} working days so far · ${t.remaining} still to come`}
          >
            {t.payable}<span className="font-normal text-surface-500"> / {row.workingDays}</span>
          </TableCell>
          <TableCell className={cn(NUM, "whitespace-nowrap")}>
            {t.present}
            {t.missedCheckouts > 0 && (
              <Badge tone="warning" className="ml-1.5" title="Checked in but never checked out — paid, worth a look before the run">
                {t.missedCheckouts} no out
              </Badge>
            )}
          </TableCell>
          <TableCell className={NUM}>{t.paidLeave}</TableCell>
          <TableCell className={cn(NUM, t.lop > 0 && "font-semibold text-[var(--color-warning-fg)]")}>{t.lop}</TableCell>
          <TableCell className={cn(NUM, t.absent > 0 && "font-semibold text-[var(--color-error-fg)]")}>{t.absent}</TableCell>
          <TableCell className={cn(NUM, "text-surface-500")}>{t.remaining}</TableCell>
        </>
      )}
      <TableCell className={cn(NUM, "border-l border-surface-200 whitespace-nowrap")}>
        {row.monthlyGross == null ? <span className="text-surface-400" title="No approved salary structure">—</span> : money(row.monthlyGross)}
      </TableCell>
      <TableCell className={cn(NUM, "whitespace-nowrap")} title={row.perDay == null ? undefined : `${money(row.monthlyGross)} ÷ ${row.workingDays} working days`}>
        {row.perDay == null ? <span className="text-surface-300">—</span> : money(row.perDay)}
      </TableCell>
      <TableCell className={cn(NUM, "whitespace-nowrap")}>
        {row.inHandMonthly == null ? <span className="text-surface-300">—</span> : money(row.inHandMonthly)}
      </TableCell>
      {basis === "month" ? (
        <TableCell className={cn(NUM, "whitespace-nowrap font-semibold")} title={row.inHandPayable == null ? undefined : `${money(row.inHandMonthly)} ÷ ${row.workingDays} × ${row.payableDays} payable days`}>
          {row.inHandPayable == null ? <span className="font-normal text-surface-300">—</span> : money(row.inHandPayable)}
        </TableCell>
      ) : (
        <>
          <TableCell className={cn(NUM, "whitespace-nowrap")} title={t.gross == null ? undefined : `${money(row.perDay)} × ${t.payable} days earned`}>
            {t.gross == null ? <span className="text-surface-300">—</span> : money(t.gross)}
          </TableCell>
          <TableCell className={cn(NUM, "whitespace-nowrap font-semibold")} title={t.inHand == null ? undefined : `${money(row.inHandMonthly)} ÷ ${row.workingDays} × ${t.payable} days earned`}>
            {t.inHand == null ? <span className="font-normal text-surface-300">—</span> : money(t.inHand)}
          </TableCell>
        </>
      )}
      <TableCell className={cn(NUM, "whitespace-nowrap")}>
        {row.ctcChange ? (
          <span title={`${row.ctcChange.reason} · effective ${row.ctcChange.effective}`}>
            {money(row.ctcChange.from)} → <span className="font-semibold">{money(row.ctcChange.to)}</span>
            <span className="block text-[11px] text-surface-500">from {row.ctcChange.effective}</span>
          </span>
        ) : <span className="text-surface-300">—</span>}
      </TableCell>
      <TableCell className={cn(NUM, "whitespace-nowrap")}>{row.reimbursement > 0 ? money(row.reimbursement) : <span className="text-surface-300">—</span>}</TableCell>
      <TableCell className="px-2 text-center">
        {row.settlement === "pending" && <Badge tone="warning" title="Full & final settlement not finalised">F&amp;F pending</Badge>}
        {row.settlement === "done" && <Badge tone="success">F&amp;F done</Badge>}
        {row.settlement === null && <span className="text-surface-300">—</span>}
      </TableCell>
      <BreakupCell id={row.id} name={row.name} onBreakup={onBreakup} />
    </TableRow>
  );
});

const ReconTableRow = memo(function ReconTableRow({ row, onBreakup }: { row: ReconRow; onBreakup: (id: string) => void }) {
  return (
    <TableRow className="group hover:bg-surface-50">
      <TableCell className={cn(stickyName, "bg-surface-0 group-hover:bg-surface-50")}>
        <div className={cn(NAME_WIDTH, "truncate font-semibold text-surface-900")} title={[row.name, row.department, row.office].filter(Boolean).join(" · ")}>{row.name}</div>
      </TableCell>
      <TableCell className={cn(stickyCode, "bg-surface-0 text-surface-600 group-hover:bg-surface-50")}>
        <div className={cn(CODE_WIDTH, "truncate")}>{row.code}</div>
      </TableCell>
      <TableCell className="px-3 text-surface-700"><div className="max-w-[160px] truncate">{row.department ?? "—"}</div></TableCell>
      <TableCell className={cn(NUM, "border-l border-surface-200")}>
        {row.payableDays == null ? "—" : <>{row.payableDays}<span className="text-surface-500"> / {row.workingDays}</span></>}
      </TableCell>
      <TableCell className={cn(NUM, "whitespace-nowrap")} title={row.monthlyGross == null ? undefined : `${money(row.monthlyGross)} ÷ ${row.workingDays} working days × ${row.payableDays} payable days`}>{money(row.expectedGross)}</TableCell>
      <TableCell className={cn(NUM, "whitespace-nowrap font-semibold")}>{money(row.uploadedGross)}</TableCell>
      <TableCell className={cn(NUM, "whitespace-nowrap", row.difference != null && row.difference !== 0 && "font-semibold", row.status === "Mismatch" && "text-[var(--color-error-fg)]")}>
        {row.difference == null ? "—" : `${row.difference > 0 ? "+" : ""}${money(row.difference)}`}
      </TableCell>
      <TableCell className="px-2 text-center">
        <Badge tone={STATUS_TONE[row.status]} title={row.note ?? undefined}>{row.status}</Badge>
      </TableCell>
      <BreakupCell id={row.id} name={row.name} onBreakup={onBreakup} />
    </TableRow>
  );
});

/**
 * Payroll Input: what payroll needs for the month (joiners, exits, payable working days,
 * LOP, salary changes, reimbursements) and the check against the uploaded payroll file.
 * The first tab of the Payroll page; HR and Payroll / Finance (salary roles) see it.
 */
export function PayrollInputPanel() {
  const [f, setF] = useUrlFilters(DEFAULTS);
  const [search, setSearch] = useDebouncedSearch(f.search, (v) => setF({ search: v }));
  const [open, setOpen] = useState<string | null>(null);
  const openState: OpenState = { open, setOpen };
  const org = useOrgMeta();

  const view = f.view === "reconciliation" ? "reconciliation" : "inputs";
  // Display only: both bases arrive on every row, so switching never refetches.
  const basis: PayBasis = f.basis === "month" ? "month" : "toDate";
  const anchor = f.anchor || reportAnchor("month");
  const range = reportRange("month", anchor);
  const month = anchor.slice(0, 7);
  const [breakupId, setBreakupId] = useState<string | null>(null);

  const params = useMemo(() => {
    const { anchor: _a, changes, view: _v, basis: _b, ...rest } = f;
    return toParams(rest, { month, view, ...(changes === "changes" ? { changesOnly: "true" } : {}) });
  }, [f, month, view]);
  const { data, cards: c, loading, error } = useReport<PayrollReport>(API, params, "PayrollInputPanel", ROW_ONLY);

  // Rows of the other tab can still be on screen while the new tab loads.
  const inputs = data?.view === "inputs" && view === "inputs" ? (data.rows as InputRow[]) : [];
  const recon = data?.view === "reconciliation" && view === "reconciliation" ? (data.rows as ReconRow[]) : [];
  const noRun = view === "reconciliation" && data?.view === "reconciliation" && !data.run.uploaded;
  const rows = view === "inputs" ? inputs : recon;

  return (
    <div className="flex flex-col gap-5">
      <CardGrid>
        <ReportStatCard icon={Users} tone="brand" label="Employees" value={c?.employees ?? "—"} caption={range.label} />
        <ReportStatCard icon={UserPlus} tone="success" label="Joiners" value={c?.joiners ?? "—"} caption="Joined this month" />
        <ReportStatCard icon={LogOut} tone="warning" label="Exits" value={c?.exits ?? "—"} caption={c ? `${c.pendingSettlements} F&F pending` : "Last working day this month"} />
        <ReportStatCard icon={TrendingUp} tone="info" label="CTC Changes" value={c?.ctcChanges ?? "—"} caption="Approved revisions" />
        <ReportStatCard icon={CalendarMinus} tone="rose" label="Total LOP" value={<>{c?.totalLop ?? "—"}<SummaryStatSuffix> days</SummaryStatSuffix></>} />
        <ReportStatCard icon={Receipt} tone="cyan" label="Reimbursements" value={c ? money(c.reimbursements) : "—"} valueClassName="text-base" caption="Approved for payroll" />
        <ReportStatCard
          icon={FileWarning} tone="danger" label="Mismatches"
          value={c?.mismatches ?? "—"}
          caption={c && c.mismatches === null ? "No payroll file uploaded" : c ? `${c.missingInPayroll} missing · ${c.notInHrms} not in HRMS` : undefined}
        />
      </CardGrid>

      <FilterBar>
        <SearchInput className="w-56" placeholder="Search name or code" value={search} onChange={(e) => setSearch(e.target.value)} />
        <OrgFilters meta={org} officeIds={f.officeIds} departmentIds={f.departmentIds} onChange={setF} openState={openState} />
        <div className="flex items-center gap-3">
          <SpecularButton onClick={() => setF({ anchor: shiftReportAnchor("month", anchor, -1) })} aria-label="Previous month" className="w-8 px-0">&larr;</SpecularButton>
          <div className="min-w-[150px] text-center text-sm font-semibold">{range.label}</div>
          <SpecularButton onClick={() => setF({ anchor: shiftReportAnchor("month", anchor, 1) })} aria-label="Next month" className="w-8 px-0">&rarr;</SpecularButton>
        </div>
        {view === "inputs" && (
          <DropdownSelect
            value={f.changes} onChange={(v) => setF({ changes: v })} options={CHANGE_OPTIONS}
            placeholder="All employees" ariaLabel="Changes" clearable={false} {...dropdownProps("changes", openState)}
          />
        )}
        {view === "inputs" && (
          <DropdownSelect
            value={basis} onChange={(v) => setF({ basis: v })} options={BASIS_OPTIONS}
            placeholder="Pay to date" ariaLabel="Pay basis" clearable={false} {...dropdownProps("basis", openState)}
          />
        )}
      </FilterBar>

      <Tabs value={view} onValueChange={(v) => setF({ view: v })}>
        <TabsList>
          <TabsTrigger value="inputs">Inputs</TabsTrigger>
          <TabsTrigger value="reconciliation">
            Reconciliation{c && c.mismatches ? <Badge tone="error" className="ml-2">{c.mismatches}</Badge> : null}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <TableToolbar
        left={data && (
          <span className="text-[12px] text-surface-600">
            {view === "inputs" && basis === "toDate" && (
              data.asOf
                ? `Pay to date as of ${fmtAsOf(data.asOf)} (today counts once marked) · `
                : `${range.label} has not started · `
            )}
            {data.run.uploaded
              ? `Payroll file: ${data.run.lineCount} lines (${data.run.sources.join(", ")})`
              : `No payroll file uploaded for ${range.label}`}
          </span>
        )}
      >
        <PageSizeSelect limit={f.limit} onLimit={(v) => setF({ limit: v })} openState={openState} />
        <ExportButton onExport={() => downloadReport(API, params, `payroll-input-${month}.xlsx`)} />
      </TableToolbar>

      <ErrorBanner message={error} />

      {noRun ? (
        <Card className="flex flex-col items-center gap-2 p-10 text-center">
          <FileWarning size={28} className="text-surface-400" />
          <p className="text-[14px] font-semibold text-surface-900">No payroll file uploaded for {range.label}</p>
          <p className="text-[13px] text-surface-500">Upload the month&apos;s payroll to compare it with the expected gross.</p>
          <Link to="/payroll?tab=runs" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand-600 hover:underline">
            <Banknote size={14} /> Upload in Payroll Uploads
          </Link>
        </Card>
      ) : (
        <ReportTableFrame loading={loading} empty={rows.length === 0} emptyText={`No employees match these filters for ${range.label}.`} contentKey={view}>
          {view === "inputs" ? (
            <Table className="w-max min-w-full">
              <TableHeader>
                <TableRow className="bg-surface-50">
                  <TableHead className={cn(stickyName, "bg-surface-50")}><div className={NAME_WIDTH}><TableScrollStart />Employee Name</div></TableHead>
                  <TableHead className={cn(stickyCode, "bg-surface-50")}><div className={CODE_WIDTH}>Emp Code</div></TableHead>
                  <TableHead className="px-3">Department</TableHead>
                  <TableHead className="px-3">Office</TableHead>
                  <TableHead className="border-l border-surface-200 px-2 text-center" title="Only when the employee joined this month">Joined</TableHead>
                  <TableHead className="px-2 text-center" title="Last working day, only when it falls this month">Exit / LWD</TableHead>
                  {basis === "month" ? (
                    <>
                      <TableHead className="border-l border-surface-200 px-2 text-center" title="Working days (weekdays less public holidays) from joining to the last working day, less LOP — out of the month's working days">Payable / Working Days</TableHead>
                      <TableHead className="px-2 text-center">LOP Days</TableHead>
                    </>
                  ) : (
                    <>
                      <TableHead className="border-l border-surface-200 px-2 text-center" title="Working days so far marked present or on approved leave, less LOP — out of the month's working days">Earned / Working Days</TableHead>
                      <TableHead className="px-2 text-center" title="Days checked in so far">Present</TableHead>
                      <TableHead className="px-2 text-center" title="Approved leave so far, less its LOP part">Paid Leave</TableHead>
                      <TableHead className="px-2 text-center" title="Loss of pay so far">LOP</TableHead>
                      <TableHead className="px-2 text-center" title="Past working days with no check-in and no approved leave — not paid">Absent</TableHead>
                      <TableHead className="px-2 text-center" title="Working days of the month still to come (today until it is marked)">Days Left</TableHead>
                    </>
                  )}
                  <TableHead className="border-l border-surface-200 px-2 text-center" title="Earning lines of the approved salary structure">Monthly Gross</TableHead>
                  <TableHead className="px-2 text-center" title="Monthly gross ÷ the month's working days">Per Day</TableHead>
                  <TableHead className="px-2 text-center" title="Gross less gratuity, PF and ESI (employee and employer) and medical insurance — the CTC breakup's take-home before tax">In-hand / Month</TableHead>
                  {basis === "month" ? (
                    <TableHead className="px-2 text-center" title="In-hand monthly ÷ working days × payable days">In-hand Payable</TableHead>
                  ) : (
                    <>
                      <TableHead className="px-2 text-center" title="Per day × days earned so far">Gross So Far</TableHead>
                      <TableHead className="px-2 text-center" title="In-hand monthly ÷ working days × days earned so far">In-hand So Far</TableHead>
                    </>
                  )}
                  <TableHead className="px-2 text-center">CTC Change</TableHead>
                  <TableHead className="px-2 text-center" title="Approved expense claims payroll should reimburse">Reimbursements</TableHead>
                  <TableHead className="px-2 text-center">Settlement</TableHead>
                  <TableHead className={BREAKUP_HEAD}>Breakup</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{inputs.map((r) => <InputTableRow key={r.id} row={r} basis={basis} onBreakup={setBreakupId} />)}</TableBody>
            </Table>
          ) : (
            <Table className="w-max min-w-full">
              <TableHeader>
                <TableRow className="bg-surface-50">
                  <TableHead className={cn(stickyName, "bg-surface-50")}><div className={NAME_WIDTH}><TableScrollStart />Employee Name</div></TableHead>
                  <TableHead className={cn(stickyCode, "bg-surface-50")}><div className={CODE_WIDTH}>Emp Code</div></TableHead>
                  <TableHead className="px-3">Department</TableHead>
                  <TableHead className="border-l border-surface-200 px-2 text-center">Payable / Working Days</TableHead>
                  <TableHead className="px-2 text-center" title="Monthly gross ÷ working days × payable days">Expected Gross</TableHead>
                  <TableHead className="px-2 text-center">Uploaded Gross</TableHead>
                  <TableHead className="px-2 text-center">Difference</TableHead>
                  <TableHead className="px-2 text-center" title="Matched means within ±1% of the expected gross">Status</TableHead>
                  <TableHead className={BREAKUP_HEAD}>Breakup</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{recon.map((r) => <ReconTableRow key={`${r.id ?? "x"}-${r.code}`} row={r} onBreakup={setBreakupId} />)}</TableBody>
            </Table>
          )}
        </ReportTableFrame>
      )}

      <SalaryBreakupDialog employeeId={breakupId} month={month} basis={view === "inputs" ? basis : "month"} onClose={() => setBreakupId(null)} />

      <ReportFooter
        meta={data?.meta} onPage={(p) => setF({ page: String(p) })}
      />
    </div>
  );
}
